import { type JSX, type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { Icon } from '@/components/icons';
import { Button, Card, Input, Skeleton, Spinner, Stepper, Textarea, WideModal } from '@/components/ui';
import { ContactListImportModal } from '@/components/contacts/ContactListImportModal';
import {
  useCampaign,
  useCreateCampaign,
  useUpdateCampaign,
} from '@/hooks/useCampaigns';
import {
  useContactLists,
  useContactsInList,
  useContactVariables,
  type ContactItem,
} from '@/hooks/useContactLists';
import { useSendersList, type SenderItem } from '@/hooks/useSenders';
import { ApiError } from '@/lib/api';
import { calculateSegments } from '@/lib/sms';

// renderTemplate substitutes `{{token}}` placeholders in an SMS body with the
// matching field of a sample contact (custom fields first, then built-ins).
// Tokens with no matching value are left literal so the preview shows exactly
// which variables would not resolve. Pure + side-effect free.
function renderTemplate(body: string, contact: ContactItem | undefined): string {
  if (!contact) return body;
  const builtins: Record<string, string | undefined> = {
    phone: contact.phone_e164,
    phone_e164: contact.phone_e164,
    country_code: contact.country_code,
  };
  return body.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (match, rawKey: string) => {
    const key = rawKey.trim();
    const custom = contact.custom_fields?.[key];
    if (custom != null && custom !== '') return custom;
    const builtin = builtins[key];
    if (builtin != null && builtin !== '') return builtin;
    return match;
  });
}

// contactLabel picks a friendly identifier for the "preview with …" caption:
// a name-ish custom field if present, otherwise the phone number.
function contactLabel(contact: ContactItem): string {
  const cf = contact.custom_fields ?? {};
  return (
    cf.first_name ||
    cf.name ||
    cf.full_name ||
    cf.last_name ||
    contact.phone_e164
  );
}

// ---------------------------------------------------------------------------
// Full-page campaign wizard. Lives at:
//   /campaigns/new          — create a new draft
//   /campaigns/:id/edit     — edit an existing draft (backend rejects
//                             non-draft edits; the list UI only links here
//                             when the campaign is editable)
//
// Replaces the in-page modal that lived in campaigns.tsx. The visual
// language follows the design handoff: stepper across the top, content
// fills the page width, sticky-ish footer with [Back] / [Cancel] / [Next].
// ---------------------------------------------------------------------------

interface SetupForm {
  name: string;
  senderID: number | null;
  listID: number | null;
  rotateSenders: boolean;
}

type StepNum = 1 | 2 | 3;

export default function CampaignWizardPage(): JSX.Element {
  const { id: idParam } = useParams<{ id?: string }>();
  const editID = idParam ? Number(idParam) : null;
  const isEdit = editID !== null && Number.isFinite(editID);

  // When editing we must wait for the campaign to load so we can prefill
  // the form. The hook is enabled regardless of `isEdit` so the cache is
  // shared with the detail page; useCampaign returns null for null ids.
  const editing = useCampaign(isEdit ? editID : null);

  // Wait until edit data is in cache before rendering the form so we don't
  // flash the empty state then jump.
  if (isEdit && editing.isLoading) {
    return (
      <div className="px-8 py-8 max-w-[960px] mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <WizardForm
      editID={isEdit ? editID : null}
      initial={
        isEdit && editing.data
          ? {
              name: editing.data.name,
              senderID: editing.data.sender_id,
              listID: editing.data.list_id,
              body: editing.data.message_template,
              rotateSenders: editing.data.rotate_senders ?? false,
            }
          : null
      }
    />
  );
}

interface InitialValues {
  name: string;
  senderID: number;
  listID: number;
  body: string;
  rotateSenders: boolean;
}

function WizardForm({
  editID,
  initial,
}: {
  editID: number | null;
  initial: InitialValues | null;
}): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const navigate = useNavigate();
  const isEdit = editID !== null;

  const senders = useSendersList();
  const lists = useContactLists();

  const [step, setStep] = useState<StepNum>(1);
  const [setup, setSetup] = useState<SetupForm>({
    name: initial?.name ?? '',
    senderID: initial?.senderID ?? null,
    listID: initial?.listID ?? null,
    rotateSenders: initial?.rotateSenders ?? false,
  });
  const [body, setBody] = useState<string>(initial?.body ?? '');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateCampaign();
  const update = useUpdateCampaign();

  // Variables for the active list — drives the chip picker on step 2.
  const variables = useContactVariables(setup.listID);

  // If `initial` arrives late (edit mode after the GET resolves),
  // re-seed the form once.
  const seededRef = useEffect(() => {
    if (initial && isEdit) {
      setSetup({
        name: initial.name,
        senderID: initial.senderID,
        listID: initial.listID,
        rotateSenders: initial.rotateSenders,
      });
      setBody(initial.body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.name, initial?.senderID, initial?.listID, initial?.body, initial?.rotateSenders]);
  void seededRef;

  const senderOptions: SenderItem[] = (senders.data?.items ?? []).filter(
    (s) => s.registration_status === 'approved' || s.registration_status === 'not_required',
  );
  const listOptions = lists.data?.items ?? [];
  const activeList = listOptions.find((l) => l.id === setup.listID) ?? null;
  const activeSender = senderOptions.find((s) => s.id === setup.senderID) ?? null;
  const seg = calculateSegments(body);

  const canStep1 = setup.name.trim().length > 0 && setup.senderID !== null && setup.listID !== null;
  const canStep2 = body.trim().length > 0;

  const onCancel = () => navigate('/campaigns');

  const submit = async () => {
    if (!setup.senderID || !setup.listID) return;
    setError(null);
    const payload = {
      name: setup.name.trim(),
      sender_id: setup.senderID,
      list_id: setup.listID,
      message_template: body,
      message_type: 'marketing' as const,
      rotate_senders: setup.rotateSenders,
    };
    try {
      let id: number;
      if (isEdit && editID !== null) {
        const updated = await update.mutateAsync({ id: editID, ...payload });
        id = updated.id;
      } else {
        const created = await create.mutateAsync(payload);
        id = created.id;
      }
      navigate(`/campaigns/${id}`);
    } catch (err) {
      if (err instanceof ApiError || err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('campaigns_err_generic'));
      }
    }
  };

  const submitting = create.isPending || update.isPending;
  const insertVar = (name: string) => {
    setBody((prev) => prev + `{{${name}}}`);
  };

  return (
    <div className="px-8 py-8 max-w-[960px] mx-auto">
      {/* Breadcrumb back to list */}
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-1 text-[12px] text-text-3 hover:text-text mb-3"
      >
        <Icon.chevronLeft width="14" height="14" />
        {t('cd_back_link')}
      </Link>

      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
          {isEdit ? t('campaigns_edit_title') : t('campaigns_wizard_title')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1.5">{t('cw_subhead')}</p>
      </header>

      {/* Stepper — full width, numbered circles connected by lines.
          Past steps are filled volt with a check; current is filled
          volt with the number; future steps are outlined. */}
      <Card padding="lg" className="mb-5">
        <Stepper
          current={step}
          steps={[t('campaigns_step_1'), t('campaigns_step_2'), t('campaigns_step_3')]}
        />
      </Card>

      {error && (
        <div
          role="alert"
          className="px-3.5 py-3 mb-4 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger"
        >
          {error}
        </div>
      )}

      {/* Step content */}
      <Card padding="lg">
        {step === 1 && (
          <Step1
            setup={setup}
            setSetup={setSetup}
            sendersLoading={senders.isLoading}
            senderOptions={senderOptions}
            listsLoading={lists.isLoading}
            listOptions={listOptions}
            lang={lang}
          />
        )}
        {step === 2 && (
          <Step2
            body={body}
            setBody={setBody}
            seg={seg}
            variables={variables.data?.variables ?? []}
            onInsertVar={insertVar}
          />
        )}
        {step === 3 && (
          <Step3
            setup={setup}
            body={body}
            seg={seg}
            activeSender={activeSender}
            activeList={activeList}
            lang={lang}
          />
        )}
      </Card>

      {/* Footer actions */}
      <div className="flex items-center justify-between mt-5">
        <div>
          {step > 1 && (
            <Button variant="secondary" onClick={() => setStep((s) => (s - 1) as StepNum)}>
              {t('campaigns_back')}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t('campaigns_cancel')}
          </Button>
          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => (s + 1) as StepNum)}
              disabled={(step === 1 && !canStep1) || (step === 2 && !canStep2)}
            >
              {t('campaigns_next')}
            </Button>
          ) : (
            <Button onClick={() => void submit()} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  <span>{isEdit ? t('campaigns_saving') : t('campaigns_creating')}</span>
                </>
              ) : isEdit ? (
                t('campaigns_save_changes')
              ) : (
                t('campaigns_create_draft')
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — name + sender + list
// ---------------------------------------------------------------------------
interface Step1Props {
  setup: SetupForm;
  setSetup: React.Dispatch<React.SetStateAction<SetupForm>>;
  sendersLoading: boolean;
  senderOptions: SenderItem[];
  listsLoading: boolean;
  listOptions: { id: number; name: string; contact_count: number }[];
  lang: string;
}

function Step1({
  setup,
  setSetup,
  sendersLoading,
  senderOptions,
  listsLoading,
  listOptions,
  lang,
}: Step1Props): JSX.Element {
  const { t } = useTranslation('default');
  const qc = useQueryClient();
  const [listModalOpen, setListModalOpen] = useState(false);
  return (
    <div className="space-y-4">
      <Field label={t('campaigns_field_name')}>
        <Input
          value={setup.name}
          onChange={(e) => setSetup((p) => ({ ...p, name: e.target.value }))}
          placeholder={t('campaigns_field_name_placeholder')}
        />
      </Field>

      <Field label={t('campaigns_field_sender')}>
        <select
          value={setup.senderID ?? ''}
          onChange={(e) =>
            setSetup((p) => ({
              ...p,
              senderID: e.target.value ? Number(e.target.value) : null,
            }))
          }
          disabled={sendersLoading}
          className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
        >
          <option value="">{t('campaigns_choose_sender')}</option>
          {senderOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.value} ({s.country_code})
            </option>
          ))}
        </select>
        {senderOptions.length === 0 && !sendersLoading && (
          <p className="text-[12px] text-text-3 mt-1.5">
            {t('cw_no_sender_note')}{' '}
            <Link to="/senders" className="text-volt-ink hover:underline">
              {t('cw_no_sender_link')}
            </Link>
          </p>
        )}
      </Field>

      {/* Sender-ID rotation — when on, the worker spreads sends round-robin
          across every approved sender assigned to the tenant (throughput +
          deliverability). sender_id above stays the required default. */}
      <div className="flex items-start justify-between gap-4 rounded-md border border-line px-3.5 py-3">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-text">{t('sms_rotate_label')}</div>
          {setup.rotateSenders && (
            <p className="text-[12px] text-text-3 mt-1">
              {t('sms_rotate_hint', { count: senderOptions.length })}
            </p>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={setup.rotateSenders}
          aria-label={t('sms_rotate_label')}
          onClick={() => setSetup((p) => ({ ...p, rotateSenders: !p.rotateSenders }))}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors outline-none focus:ring-[3px] focus:ring-volt-glow ${
            setup.rotateSenders ? 'bg-volt-500' : 'bg-line-strong'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              setup.rotateSenders ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      <Field label={t('campaigns_field_list')}>
        <select
          value={setup.listID ?? ''}
          onChange={(e) =>
            setSetup((p) => ({
              ...p,
              listID: e.target.value ? Number(e.target.value) : null,
            }))
          }
          disabled={listsLoading}
          className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
        >
          <option value="">{t('campaigns_choose_list')}</option>
          {listOptions.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} ({l.contact_count.toLocaleString(lang)})
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setListModalOpen(true)}
          className="mt-1.5 text-[13px] font-medium text-volt-ink hover:underline"
        >
          + {t('cw_create_list_btn')}
        </button>
      </Field>

      {listModalOpen && (
        <WideModal title={t('cw_create_list_modal_title')} onClose={() => setListModalOpen(false)}>
          <ContactListImportModal
            onCreated={(listId) => {
              setSetup((p) => ({ ...p, listID: listId }));
              setListModalOpen(false);
              void qc.invalidateQueries({ queryKey: ['contact-lists'] });
            }}
          />
        </WideModal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — message body + variables
// ---------------------------------------------------------------------------
interface Step2Props {
  body: string;
  setBody: (s: string) => void;
  seg: { characters: number; encoding: string; segments: number };
  variables: string[];
  onInsertVar: (name: string) => void;
}

function Step2({ body, setBody, seg, variables, onInsertVar }: Step2Props): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <div>
      <Field label={t('campaigns_field_message')}>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          placeholder={t('campaigns_field_message_placeholder')}
        />
      </Field>
      <div className="flex items-center justify-between mt-1.5 text-[12px]">
        <span className="text-text-4">{t('campaigns_field_message_help')}</span>
        <span className="num text-text-3">
          {seg.characters} · {seg.encoding} · {seg.segments} seg
        </span>
      </div>

      {variables.length > 0 && (
        <div className="mt-5">
          <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
            {t('campaigns_available_vars')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {variables.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => onInsertVar(v)}
                className="mono px-2.5 py-1 bg-volt-50 hover:bg-volt-100 border border-volt-200 rounded-md text-[12px] text-volt-700 transition-colors"
                title={t('campaigns_insert_var')}
              >
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — review
// ---------------------------------------------------------------------------
interface Step3Props {
  setup: SetupForm;
  body: string;
  seg: { characters: number; encoding: string; segments: number };
  activeSender: SenderItem | null;
  activeList: { name: string; contact_count: number } | null;
  lang: string;
}

function Step3({ setup, body, seg, activeSender, activeList, lang }: Step3Props): JSX.Element {
  const { t } = useTranslation('default');
  const contacts = useContactsInList({ listID: setup.listID });
  const firstContact = contacts.data?.items?.[0];
  const rendered = renderTemplate(body, firstContact);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
          {t('cw_review_summary')}
        </div>
        <dl className="space-y-2 text-[13px]">
          <ReviewRow label={t('campaigns_field_name')} value={setup.name} />
          <ReviewRow
            label={t('campaigns_field_sender')}
            value={activeSender ? `${activeSender.value} (${activeSender.country_code})` : '—'}
          />
          <ReviewRow
            label={t('campaigns_field_list')}
            value={
              activeList
                ? `${activeList.name} · ${activeList.contact_count.toLocaleString(lang)} ${t('cw_recipients_short')}`
                : '—'
            }
          />
          <ReviewRow label={t('campaigns_segments_each')} value={`${seg.segments} (${seg.encoding})`} />
        </dl>
        <div
          className="px-3 py-2.5 mt-4 bg-info-bg border border-info-bd rounded-md text-[12px] text-info"
        >
          {t('campaigns_create_only_note')}
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
            {t('campaigns_message_preview')}
          </div>
          <div className="px-3.5 py-3 bg-surface-2 border border-line rounded-md text-[13px] text-text whitespace-pre-wrap min-h-[120px]">
            {body}
          </div>
        </div>
        {firstContact && (
          <div>
            <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
              {t('cw_preview_with', { name: contactLabel(firstContact) })}
            </div>
            <div className="px-3.5 py-3 bg-volt-50 border border-volt-200 rounded-md text-[13px] text-text whitespace-pre-wrap">
              {rendered}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field + ReviewRow helpers
// ---------------------------------------------------------------------------
function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-line last:border-0">
      <dt className="text-text-3">{label}</dt>
      <dd className="text-text font-medium text-right truncate">{value}</dd>
    </div>
  );
}
